import { useCallback, useEffect, useRef, useState } from "react";

// Native, on-device Turkish text-to-speech via `window.speechSynthesis`.
//
//   * Universal on all target browsers (Chrome / Edge / Safari desktop
//     and mobile). Free, on-device, zero audio egress.
//   * Speaks strictly on explicit user tap. No autoplay, no background.
//   * Prefers a `tr-TR` voice; falls back to any `tr-*` locale. If no
//     Turkish voice is exposed by the OS we surface `turkishAvailable:
//     false` and the caller renders a disabled Speak control — we never
//     silently substitute a non-Turkish voice.
//   * Tracks a single "currently speaking" id at a time; a new speak()
//     cancels the previous utterance.
export function useVoicePlayback() {
  const synth = typeof window !== "undefined" ? window.speechSynthesis : null;
  const supported = Boolean(synth);

  const [turkishAvailable, setTurkishAvailable] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const voiceRef = useRef(null);

  const pickVoice = useCallback(() => {
    if (!synth) return null;
    const voices = synth.getVoices() || [];
    // Strong preference: exact tr-TR.
    let v = voices.find((x) => (x.lang || "").toLowerCase() === "tr-tr");
    // Otherwise any Turkish variant.
    if (!v) v = voices.find((x) => (x.lang || "").toLowerCase().startsWith("tr"));
    return v || null;
  }, [synth]);

  useEffect(() => {
    if (!synth) return;
    const load = () => {
      const v = pickVoice();
      voiceRef.current = v;
      setTurkishAvailable(Boolean(v));
    };
    load();
    // Chrome loads voices asynchronously.
    try {
      synth.addEventListener?.("voiceschanged", load);
    } catch (_) { /* older Safari */ }
    return () => {
      try {
        synth.removeEventListener?.("voiceschanged", load);
        synth.cancel();
      } catch (_) { /* noop */ }
    };
  }, [pickVoice, synth]);

  const stop = useCallback(() => {
    if (!synth) return;
    try { synth.cancel(); } catch (_) { /* noop */ }
    setSpeakingId(null);
  }, [synth]);

  const speak = useCallback(
    (id, text) => {
      if (!supported || !text) return;
      const voice = voiceRef.current || pickVoice();
      if (!voice) return; // caller should have disabled the button.
      try {
        synth.cancel();
        const utt = new window.SpeechSynthesisUtterance(text);
        utt.voice = voice;
        utt.lang = voice.lang || "tr-TR";
        utt.rate = 1;
        utt.pitch = 1;
        utt.volume = 1;
        utt.onend = () => {
          setSpeakingId((current) => (current === id ? null : current));
        };
        utt.onerror = () => {
          setSpeakingId((current) => (current === id ? null : current));
        };
        setSpeakingId(id);
        synth.speak(utt);
      } catch (_) {
        setSpeakingId(null);
      }
    },
    [pickVoice, supported, synth],
  );

  return {
    supported,
    turkishAvailable,
    speakingId,
    speak,
    stop,
  };
}
